// Legacy Fach-Master IDs sind stabil bis Ende der Migration.
// Ist die Master-Liste fuer das Mapping vom alten ins neue System –
// Legacy-IDs (z. B. 6 = Klavier) werden NICHT als neue System-IDs benutzt.
export const LEGACY_FACH_MASTER: Record<number, string> = {
  1: 'Gitarre',
  2: 'Gesang',
  3: 'Schlagzeug',
  4: 'Bass',
  5: 'Tonstudio',
  6: 'Klavier',
  7: 'Keyboard',
  8: 'Beatbox',
  11: 'Rock Academy - Bandunterricht',
  13: 'Querflöte',
  14: 'Klarinette',
  15: 'Saxofon',
  16: 'Musikalische Früherziehung',
  17: 'Musikgarten',
  20: 'Gehörbildung',
  21: 'Blockflöte',
  22: 'Musikzirkel',
  23: 'Workshop',
  24: 'Geige',
  25: 'Studienvorbereitung',
  26: 'Komposition',
  27: 'Kinderchor',
  28: 'Trompete',
  29: 'Bratsche',
  30: 'Streicherensemble',
  31: 'Djembé',
  35: 'Lehrerfortbildung',
  36: 'Bläserensemble',
  37: 'Cello',
  38: 'Akkordeon',
  39: 'Studiotechnik',
  40: 'Kontrabass',
  43: 'Chor',
  45: 'Posaune',
  46: 'Cajon',
  47: 'Bläserklasse',
  48: 'Ukulele',
  49: 'Didgeridoo',
  50: 'Tuba',
  52: 'Harmonielehre',
  54: 'E-Gitarre',
  55: 'Vocal Coaching',
  56: 'Songwriting',
  57: 'Hackbrett',
  58: 'Harfe',
  59: 'Melodica',
  60: 'Mundharmonika',
  62: 'Zither',
  63: 'Steirische',
  64: 'Tenor-/Baritonhorn',
  65: 'Waldhorn',
  66: 'Musiktheorie',
  67: 'Percussion',
  68: 'Oboe',
  69: 'Rap',
  70: 'Freestyle',
  71: 'Mandoline',
  72: 'Dudelsack',
  73: 'Yoga',
  74: 'FIT IN dance - Musik und Tanz',
  75: 'Jazzdance',
  76: 'Hip Hop',
  77: 'Breakdance',
  78: 'Kidsdance',
  79: 'Musik am PC',
  80: 'Kirchenorgel',
};

/**
 * Liefert den Fach-Namen aus dem Legacy-Master, fallback auf den ggf.
 * mitgelieferten Namen (oder leerer String). NICHT die Legacy-ID als
 * neue System-ID interpretieren.
 */
export const resolveLegacyFachName = (
  legacyFachId: number | null | undefined,
  legacyFachName?: string | null,
): string => {
  if (typeof legacyFachId === 'number' && legacyFachId > 0 && LEGACY_FACH_MASTER[legacyFachId]) {
    return LEGACY_FACH_MASTER[legacyFachId];
  }
  return String(legacyFachName ?? '').trim();
};

type WizardStepFlags = {
  data_check?: { confirmed?: boolean };
  linked_people_check?: { confirmed?: boolean };
  courses_history_check?: { confirmed?: boolean };
};

type ContractAdjustmentWizardSnapshot = {
  action_type?: 'contract_adjustment';
  total_steps?: number;
  completed_steps?: number[];
  current_step?: number;
  wp_user_id?: number;
  last_saved_at?: string | null;
};

export type OfficeWizardSnapshot = {
  step_data?: WizardStepFlags;
  last_saved_at?: string | null;
};

export type AnyWizardSnapshot =
  | { type: 'legacy'; snapshot: OfficeWizardSnapshot }
  | { type: 'contract_adjustment'; snapshot: ContractAdjustmentWizardSnapshot };

export const formatSavedAt = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('de-DE');
};

export const getWizardSnapshot = (snapshot: unknown): AnyWizardSnapshot | null => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const payload = snapshot as Record<string, unknown>;
  const contractWizard = payload.office_contract_adjustment_wizard;
  if (contractWizard && typeof contractWizard === 'object') {
    return {
      type: 'contract_adjustment',
      snapshot: contractWizard as ContractAdjustmentWizardSnapshot,
    };
  }
  const officeWizard = payload.office_wizard;
  if (officeWizard && typeof officeWizard === 'object') {
    return { type: 'legacy', snapshot: officeWizard as OfficeWizardSnapshot };
  }
  return null;
};

export const getWizardMissingSteps = (wizard: AnyWizardSnapshot | null): string[] => {
  if (!wizard) return [];
  if (wizard.type === 'contract_adjustment') {
    const total = Math.max(1, Number(wizard.snapshot.total_steps ?? 7));
    const completed = new Set(
      (Array.isArray(wizard.snapshot.completed_steps) ? wizard.snapshot.completed_steps : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value >= 1 && value <= total),
    );
    const missing: string[] = [];
    for (let i = 1; i <= total; i += 1) {
      if (!completed.has(i)) missing.push(`Schritt ${i}`);
    }
    return missing;
  }

  const steps = wizard.snapshot.step_data;
  const missing: string[] = [];
  if (!steps?.data_check?.confirmed) missing.push('Datencheck');
  if (!steps?.linked_people_check?.confirmed) missing.push('Zuordnungen');
  if (!steps?.courses_history_check?.confirmed) missing.push('Kurse/Historie');
  return missing;
};

export const getWizardSavedAt = (wizard: AnyWizardSnapshot | null): string | null => {
  if (!wizard) return null;
  return wizard.snapshot.last_saved_at ?? null;
};

export type MigrationWorkflowState =
  | 'vertragsanpassung'
  | 'entscheidung'
  | 'anmeldung'
  | 'angebot'
  | 'unterricht';

export type MigrationWorkflowResult = {
  stepIndex: number;
  state: MigrationWorkflowState;
  anmeldungStatus: 'offen' | 'gestartet' | 'abgeschlossen';
  angebotStatus: 'offen' | 'gesendet' | 'angenommen' | 'abgeschlossen';
};

type MigrationCaseLike = {
  status?: string;
  migration_phase?: string | null;
  snapshot?: Record<string, unknown> | null;
};

/**
 * Leitet aus Status, Migration-Phase und Snapshot einen 5-Phasen-Workflow ab,
 * der dem Probestunden-Workflow visuell entspricht:
 *  Vertragsanpassung → Entscheidung → Anmeldung → Angebot → Unterricht
 *
 * Wichtig: Das Customer-Confirm legt zwar Enrollment + geplante Charge an,
 * der eigentliche Auftrag (Order) muss aber noch an den Lehrer versendet
 * und nach dessen Accept vom Office bestätigt werden. Erst dann läuft der
 * Unterricht. Die Phase „Angebot" repräsentiert genau diesen Zwischenraum
 * (Office sendet Order → Lehrer akzeptiert → Office bestätigt).
 */
export const getMigrationWorkflowState = (item: MigrationCaseLike): MigrationWorkflowResult => {
  const snapshot = (item.snapshot ?? {}) as Record<string, unknown>;
  const office = (snapshot.office_contract_adjustment ?? null) as { payload?: unknown; updated_at?: string } | null;
  const customer = (snapshot.customer_contract_adjustment ?? null) as
    | {
        confirmed_at?: string;
        finalization?: {
          enrollment_id?: number;
          charge_scheduled?: boolean;
          order_id?: number;
          offer_sent_at?: string;
          order_accepted_at?: string;
          order_confirmed_at?: string;
        };
      }
    | null;
  const finalization = customer?.finalization ?? null;

  const officeDone = Boolean(office?.payload);
  const status = String(item.status ?? '');
  const onboardingStarted = status === 'onboarding_gestartet' || status === 'migriert';
  const customerConfirmed = Boolean(customer?.confirmed_at);
  // Order-Lifecycle im Snapshot: offer_sent_at schreibt send-offer, order_accepted_at
  // die Annahme der Lehrkraft, order_confirmed_at die Bestätigung des Büros
  // (backend_fastapi/app/services/migration_case_state.py).
  const offerSent = Boolean(finalization?.offer_sent_at);
  const orderAccepted = Boolean(finalization?.order_accepted_at);
  const orderConfirmed = Boolean(finalization?.order_confirmed_at);

  let stepIndex = 0; // Vertragsanpassung
  if (officeDone) stepIndex = 1; // Entscheidung (Office-Wizard fertig, Mail an Kunde)
  if (onboardingStarted) stepIndex = 2; // Anmeldung (Kunde hat Flow gestartet)
  if (customerConfirmed) stepIndex = 3; // Angebot (Kunde fertig, Office sendet Order an Lehrer)
  if (orderAccepted) stepIndex = 4; // Unterricht (Lehrer hat angenommen)
  if (orderConfirmed) stepIndex = 4; // Unterricht (Office hat Order bestätigt)

  const states: MigrationWorkflowState[] = ['vertragsanpassung', 'entscheidung', 'anmeldung', 'angebot', 'unterricht'];

  let anmeldungStatus: MigrationWorkflowResult['anmeldungStatus'] = 'offen';
  if (onboardingStarted) anmeldungStatus = 'gestartet';
  if (customerConfirmed) anmeldungStatus = 'abgeschlossen';

  // Angebots-Status bezieht sich nur noch auf den Lehrer-Auftrag (Order):
  //   offen         — Order existiert (nach Customer-Confirm) aber nicht versendet
  //   gesendet      — Order an Lehrer raus, wartet auf Lehrer-Accept
  //   angenommen    — Lehrer hat akzeptiert, Office muss noch bestätigen
  //                   ("Unterricht bestätigen"-Button erscheint in der Card)
  //   abgeschlossen — Office hat Order bestätigt (Unterricht läuft)
  let angebotStatus: MigrationWorkflowResult['angebotStatus'] = 'offen';
  if (offerSent) angebotStatus = 'gesendet';
  if (orderAccepted) angebotStatus = 'angenommen';
  if (orderConfirmed) angebotStatus = 'abgeschlossen';

  return {
    stepIndex,
    state: states[stepIndex],
    anmeldungStatus,
    angebotStatus,
  };
};
